// ============================================================
// PractitionerColorPicker.tsx
// S5 (86d420t5u) — the shared practitioner calendar-colour swatch picker, used by BOTH the
// Team settings modal (TeamMemberInfoTab, mode="team") and a practitioner's own profile page
// (UserProfileProfessional). Built once so the two hosts cannot drift.
//
// Mirrors the interaction shape of the theme accent picker at
// src/pages/settings/themes/ThemeSwitcher.tsx:108-150 — 36px circles, a tooltip naming the
// colour, a ring + tick on selection — but:
//   - the palette is fetched from auth's GET /admin/staff/color-palette (S2), never
//     hardcoded (design decision D6 — no second copy of the 25 accent colours in src/)
//   - the tick's colour comes from getReadableTextColor() rather than ThemeSwitcher's
//     hardcoded `isLight` array
//   - a swatch already held by someone else renders disabled, with a tooltip naming the
//     holder
// ============================================================

import React from "react";
import { Tooltip, Spin, Button, Typography } from "antd";
import { CheckOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { fetchPractitionerColorPalette } from "../../api/admin/teamApi";
import { getReadableTextColor } from "../../utils/colorUtils";
import { PRACTITIONER_COLOR_PALETTE_QUERY_KEY } from "../../types/admin/PractitionerColorTypes";
import type { UserUuid } from "../../types/identity";

const { Text } = Typography;

export interface PractitionerColorPickerProps {
	/** The practitioner's currently stored colour ("#rrggbb"), or null/unset. */
	value: string | null;
	/** Called with the chosen hex, or null when "Clear" is used. */
	onChange: (hex: string | null) => void;
	/**
	 * The id of the team member this picker is shown FOR — not necessarily the logged-in
	 * user (an admin editing someone else's team info passes that member's id). A swatch
	 * already held by this id renders as selectable (their own colour, not a conflict); a
	 * swatch held by anyone else renders disabled.
	 */
	currentUserId: UserUuid;
}

const PractitionerColorPicker: React.FC<PractitionerColorPickerProps> = ({ value, onChange, currentUserId }) => {
	const { data, isLoading, isError } = useQuery({
		queryKey: PRACTITIONER_COLOR_PALETTE_QUERY_KEY,
		queryFn: fetchPractitionerColorPalette,
	});

	if (isLoading) {
		return <Spin size="small" data-testid="practitioner-color-picker-loading" />;
	}

	if (isError || !data) {
		return (
			<Text type="danger" style={{ fontSize: 13 }} data-testid="practitioner-color-picker-error">
				Unable to load the colour palette.
			</Text>
		);
	}

	const normalizedValue = value ? value.toLowerCase() : null;

	return (
		<div data-testid="practitioner-color-picker">
			<div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
				{data.palette.map((entry) => {
					const hexLower = entry.hex.toLowerCase();
					const isSelected = normalizedValue === hexLower;
					const heldByOther = !!entry.in_use_by && entry.in_use_by.user_id !== currentUserId;
					const tickColor = getReadableTextColor(entry.hex);
					const tooltipTitle = heldByOther ? `Taken by ${entry.in_use_by!.name}` : entry.name;

					return (
						<Tooltip key={entry.key} title={tooltipTitle} mouseEnterDelay={0.4}>
							<div
								data-testid={`practitioner-color-swatch-${entry.key}`}
								role="button"
								tabIndex={heldByOther ? -1 : 0}
								aria-disabled={heldByOther}
								onClick={() => {
									if (heldByOther) return;
									onChange(entry.hex);
								}}
								onKeyDown={(e) => {
									if (heldByOther) return;
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										onChange(entry.hex);
									}
								}}
								style={{
									width: 36,
									height: 36,
									borderRadius: "50%",
									backgroundColor: entry.hex,
									cursor: heldByOther ? "not-allowed" : "pointer",
									opacity: heldByOther ? 0.4 : 1,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									boxShadow: isSelected
										? `0 0 0 3px white, 0 0 0 5px ${entry.hex}`
										: "0 1px 3px rgba(0,0,0,0.25)",
									transition: "box-shadow 0.15s ease, transform 0.15s ease",
									transform: isSelected ? "scale(1.15)" : "scale(1)",
								}}
							>
								{isSelected && (
									<CheckOutlined
										data-testid={`practitioner-color-tick-${entry.key}`}
										style={{ color: tickColor, fontSize: 14, fontWeight: 700 }}
									/>
								)}
							</div>
						</Tooltip>
					);
				})}
			</div>
			<Button
				type="link"
				size="small"
				icon={<CloseCircleOutlined />}
				onClick={() => onChange(null)}
				style={{ paddingLeft: 0, marginTop: 8 }}
				data-testid="practitioner-color-clear"
			>
				Clear (use automatic colour)
			</Button>
		</div>
	);
};

export default PractitionerColorPicker;
